import type { ClientField, ClientUnit, QuestionnaireDefinition } from "./types";

/**
 * Participant-facing wording for the frozen 1.1.1 questionnaire.
 *
 * This layer changes display strings only. Answer IDs, option sets and order,
 * selection limits, optionality, privacy and scoring remain canonical.
 * Submission confirmation strings also remain canonical: the API uses their
 * exact text as identifiers. A rendered label must never replace that value.
 */
type FieldCopy = Partial<Pick<ClientField, "prompt" | "note" | "helpText" | "leftLabel" | "rightLabel">> & {
  optionLabels?: Readonly<Record<string, string>>;
};

const SECTION_TITLES: Readonly<Record<string, string>> = {
  A: "日常相处",
  B: "做决定，了解彼此",
  C: "隐私与边界",
  D: "冲突之后",
  E: "关系的进展",
  F: "在意的事与其他补充",
};

// These names also identify units in the answer review. The questionnaire
// renderer decides whether a heading adds information beside the actual question.
const UNIT_TITLES: Readonly<Record<string, string>> = {
  A01: "自由的周末",
  A02: "联系与回复",
  A03: "平时聊些什么",
  A04: "安静地待在一起",
  A05: "表达在意，感受到在意",
  A06: "情绪低落的时候",
  A07: "陪伴与独处",
  A08: "一起做些事",
  A09: "作息不同的时候",
  B01: "一起做决定",
  B02: "意见不同的时候",
  B03: "兴趣与想法",
  B04: "幽默与亲近感",
  C01: "隐私与公开",
  C02: "说不的时候",
  C03: "关心与行程",
  C04: "与可能互相吸引的人单独相处",
  D01: "发生冲突的时候",
  D02: "伤害发生之后",
  D03: "争执中需要暂停的时候",
  E01: "把关系说清楚",
  E02: "从聊天到见面",
  E03: "身体亲密",
  E04: "还没约定只和彼此交往的时候",
  F01: "难以接受的行为",
  F02: "最在意的事",
  F03: "可以接受的差异",
  F04: "想继续了解一个人的瞬间",
  F05: "逐渐失去兴趣的时候",
  F06: "还想补充的事",
};

const FIELD_COPY: Readonly<Record<string, FieldCopy>> = {
  "A01.leisure_tendency": {
    prompt: "过去三个月里，如果周末没有必须做的事，你最常怎么过？",
    optionLabels: {
      purposeful_outing: "选个地方去，比如看展、看电影，或到大自然里走走",
      other: "其他",
    },
  },
  "A02.natural_frequency": {
    prompt: "关系稳定、没有发生冲突时，多久联系一次对方让你觉得最自然？",
  },
  "A02.minimum_frequency": {
    prompt: "你最低能接受怎样的联系频率？",
  },
  "A02.reply_tolerance": {
    prompt: "一条普通消息，多久没收到回复会开始明显影响你的感受？",
    optionLabels: {
      consistency_matters: "多久回复并不重要，我更在意对方平时的回复是否稳定",
    },
  },
  "A02.response_availability": {
    prompt: "没有特殊情况时，你通常能在多久内回复普通消息？",
    optionLabels: {
      hours: "通常几小时内会回",
      same_day: "通常当天会回",
      next_day: "有时会到第二天才回",
      two_three_days: "两三天没回复，对我来说也可能自然发生",
      signals_long_unavailability: "时间不太固定，但会忙很久时，我通常会提前说",
      hard_to_predict: "很难长期保持一个可预测的回复节奏",
    },
  },
  "A03.communication_supply": {
    prompt: "关系稳定后，哪种聊天方式是你平时最容易坚持的？",
    optionLabels: { rarely_maintain: "即使几天没聊天，也不会自然而然地想起要联系对方" },
  },
  "A03.communication_need": {
    prompt: "你希望对方平时怎么和你交流？",
    optionLabels: {
      important_only: "重要的事告诉我就好，不需要交代每天做了什么",
    },
  },
  "A03.frequency_depth": {
    prompt: "如果不能兼顾，你更在意哪一边？",
    leftLabel: "经常聊，主要聊轻松的日常",
    rightLabel: "聊得没那么频繁，但能聊得很深",
  },
  "A04.silence_interpretation": {
    prompt: "和亲密的人待在同一个空间，各做各的事，一个小时都没说话。你更可能有什么感觉？",
    optionLabels: {
      should_talk: "会开始想，是不是该说点什么",
      distance: "容易觉得彼此有些疏远了",
    },
  },
  "A05.care_supply": {
    prompt: "很在意一个人时，你最自然会怎么表达？",
    optionLabels: {
      practical_help: "记住细节，主动帮上实际的忙",
      vulnerability: "分享自己的想法、脆弱的一面和私下的生活",
    },
  },
  "A05.care_need": {
    prompt: "对方怎么做，最容易让你感到被重视？",
    optionLabels: {
      time: "花时间陪我，我需要时尽量在身边",
      practical_help: "记住细节，主动帮上实际的忙",
      vulnerability: "分享自己的想法、脆弱的一面和私下的生活",
      stable_response: "平时回应稳定，不让我反复猜测关系状态",
    },
  },
  "A06.distress_need": {
    prompt: "情绪低落，还没准备好把事情完整说出来时，你更希望亲密的人怎么做？",
    optionLabels: {
      available_no_pressure: "让我知道 TA 在，但不逼我解释",
      normal_treatment: "像平常一样相处，不特别放大我的情绪",
    },
  },
  "A06.distress_supply": {
    prompt: "亲密的人明显情绪低落，却说“没事”，你通常会怎么做？",
    optionLabels: {
      available_listen: "告诉对方我愿意听，由 TA 决定什么时候说",
      quiet_company: "安静地陪着，不主动处理",
      normal_treatment: "像平常一样相处，不过分追问，也不刻意特别对待",
    },
  },
  "A07.integration_preference": {
    prompt: "关系稳定后，你希望两个人的日常生活有多少交集？",
    optionLabels: {
      stable_shared_time: "各自有自己的生活，也稳定地留出相处的时间",
      many_shared_dailies: "日常的许多事一起做，也保留个人空间",
    },
  },
  "A07.contact_adaptation": {
    prompt: "对方比你更需要陪伴和联系时，你通常愿意怎么调整？",
    optionLabels: {
      increase_investment: "多花不少时间和心思，让对方安心",
      feels_pressure: "时间久了，这会让我感到压力",
    },
  },
  "A07.closeness_space_tradeoff": {
    prompt: "长期处在什么样的关系里，你更容易疲惫？",
    rightLabel: "个人空间足够，却很少有一起生活的感觉",
  },
  "A07.space_expression": {
    prompt: "想独处时，一个重要的人又希望你陪着，你通常会怎么做？",
    optionLabels: {
      clear_with_return: "说明自己需要独处，也告诉对方什么时候再联系",
      state_no_timing: "会说现在不方便，但没法确定什么时候再联系",
      feel_intruded_pull_away: "容易觉得自己的空间被打扰，之后更想拉开距离",
    },
  },
  "A08.shared_experience_need": {
    prompt: "在一段关系里，一起出门、旅行、看展、看电影或探索新地方，对你来说有多重要？",
    optionLabels: {
      sometimes: "偶尔有就好",
    },
  },
  "A08.shared_experience_supply": {
    prompt: "关系稳定后，说到一起出门、探索或创造共同的体验，你平时更容易做到哪一种？",
    optionLabels: {
      initiate_regularly: "我会比较自然地主动提议，也能稳定地一起做这些事",
      participate_if_proposed: "不一定由我发起，但只要我们其中一人提出，我通常愿意参与",
      occasional: "偶尔可以，但很难形成稳定的节奏",
      home_low_intensity: "更喜欢一起待在家、散步，或做些不太费力的活动",
      unsure: "最近的生活状况让我很难判断",
    },
  },
  "A09.schedule_overlap_need": {
    prompt: "如果和伴侣作息差很多，比如一个经常凌晨睡，一个习惯早睡，你会怎么想？",
    optionLabels: {
      partial_overlap: "希望慢慢调整，让一部分生活时间能合得上",
      difference_hurts: "长期作息不同，会让我明显少了共同生活的感觉",
      need_similar: "我需要伴侣和我的作息相近",
    },
  },
  "A09.schedule_coordination_supply": {
    prompt: "如果和伴侣作息差很多，你通常愿意怎么协调？",
    optionLabels: {
      protect_anchors: "固定留出几个重要的时间，一起度过",
      adjust_part: "愿意调整一部分作息，让彼此有些时间能合得上",
      current_schedule_unstable: "目前工作或生活不太规律，很难答应固定的安排",
    },
  },
  "B01.planning_supply": {
    prompt: "安排约会、旅行或一起做的事时，你通常会怎么做？",
    optionLabels: {
      lead: "主动提出具体安排，也推动我们去做",
      co_create: "喜欢两个人一边商量，一边想好怎么做",
    },
  },
  "B01.replanning_behavior": {
    prompt: "原来的计划突然行不通，需要很快重新决定时，你通常会怎么做？",
    optionLabels: {
      take_over: "马上接手，重新安排",
      lose_momentum: "容易没了继续安排的劲头，倾向于取消",
    },
  },
  "B01.direction_need": {
    prompt: "两个人都不知道接下来该怎么做时，你更希望对方怎么做？",
    optionLabels: {
      strong_direction: "能自然地想出方向，也主动推动我们去做",
      co_decide: "和我一起商量，不需要谁来主导",
    },
  },
  "B02.disagreement_preference": {
    prompt: "聊到电影、文化、技术或社会问题，发现意见很不一样时，对方怎么回应最可能让你仍有兴趣？",
    optionLabels: {
      explain_reasoning: "能讲清楚自己为什么这样判断",
      understand_me: "愿意认真理解我的立场",
      show_blind_spot: "能指出我没注意到的地方",
      not_personalize: "就事论事，不因为意见不同就评价我的为人",
      use_humor: "能用幽默化解紧张",
      important_alignment: "我更希望在重要的事情上，我们的判断本来就大致一致",
    },
  },
  "B02.negotiation_behavior": {
    prompt: "一件要共同决定的事，两个人的方案很不一样，也都希望自己的意见被认真考虑。你通常会怎么做？",
    optionLabels: {
      third_option: "一起改出第三种安排，让双方都能接受",
    },
  },
  "B03.taste_vs_interest": {
    prompt: "如果只能优先选一种，你更希望哪一边？",
    leftLabel: "喜欢相同的书、音乐、电影和活动",
    rightLabel: "兴趣可以不同，但对好坏、真诚与造作的判断相近",
  },
  "B03.important_share_need": {
    prompt: "把一件对自己很重要的作品、音乐或想法分享给对方，TA 的反应却很平淡。你最在意什么？",
    optionLabels: {
      engage_and_explain: "TA 有没有认真了解过，愿不愿意说说自己的感受",
      distance_if_chronic: "如果重要的事长期没法分享，我会觉得彼此疏远了",
      separate_worlds_ok: "TA 不必理解，我们可以各有各的世界",
    },
  },
  "B03.world_integration_need": {
    prompt: "在一段关系里，你希望怎样了解、参与彼此的精神世界？",
    optionLabels: {
      mostly_shared: "参与、了解彼此大部分的兴趣和想法",
      some_shared: "有一些共同的兴趣和想法，也保留各自的部分",
      exchange_discoveries: "各有各的世界，也愿意分享重要的发现",
      comfort_over_understanding: "不必了解彼此大部分的兴趣，生活过得舒服更重要",
    },
  },
  "B03.world_entry_supply": {
    prompt: "对方分享了你原本不了解、但对 TA 很重要的作品、兴趣或想法，你通常会怎么做？",
    optionLabels: {
      active_explore: "主动去了解，也会接着问更多",
      basic_understand_why: "花些时间了解基本内容，也弄明白它为什么对 TA 重要",
      serious_listen_remember: "即使自己兴趣不大，也会认真听，记住这对 TA 很重要",
      respect_separate_worlds: "尊重这件事对 TA 的重要性，但更喜欢各有各的世界",
      hard_if_uninterested: "如果话题本身不吸引我，通常很难继续花心思",
    },
  },
  "B04.humor_preferences": {
    prompt: "什么样的幽默会让你觉得亲近？",
    optionLabels: {
      observation: "敏锐地发现日常里有意思的细节",
      absurd: "荒谬、无厘头或超现实的表达",
      self_deprecating: "自嘲，说说自己不完美的地方",
      language_logic: "巧妙地玩转语言、概念或逻辑",
      dark: "黑色幽默，或用幽默谈沉重的现实",
    },
  },
  "C01.phone_privacy": {
    prompt: "和亲密的人相处，你对手机隐私的想法更接近哪一种？",
    optionLabels: {
      no_exchange_temp_use: "不需要交换密码，临时用一下对方的手机没问题",
    },
  },
  "C01.transparency_orientation": {
    prompt: "谈到信任，你更接近哪一边？",
    leftLabel: "既然信任，没出问题就不必主动解释",
    rightLabel: "信任也需要平时把事情说清楚，少让彼此猜测",
  },
  "C01.public_visibility": {
    prompt: "你希望这段关系公开到什么程度？",
    optionLabels: {
      private_no_social: "更希望保留在私下，不发到社交媒体上",
      no_public_marker: "不希望任何公开关系的标识影响我们之间的相处",
      contextual: "要看双方现实中的处境",
    },
  },
  "C02.direction_consideration": {
    prompt: "哪一种情况更容易让你失去兴趣？",
  },
  "C02.control_threshold": {
    prompt: "从哪一步开始，你会觉得对方在控制你？选出第一个无法接受的行为。",
    optionLabels: {
      small_final_after_discussion: "商量之后，在小事上主动拍板",
      decide_personal_without_consent: "未经同意，替我安排时间或决定我自己的事",
    },
  },
  "C02.response_to_no": {
    prompt: "你提出一个共同安排，对方明确说“我不想这样”，你通常会怎么做？",
    optionLabels: {
      accept_change: "接受对方不愿意，换个方向",
      offer_without_pressure: "再提一个选择，但不会继续施压",
    },
  },
  "C03.information_threshold": {
    prompt: "亲密的人想知道你和谁见面、去了哪里、什么时候回家。从哪一步开始，你会觉得这不再只是关心？选出第一个无法接受的行为。",
    optionLabels: {
      major_change_notice: "希望我在行程有明显变化时说一声",
      every_outing_notice: "希望我每次出门前都说一声",
    },
  },
  "C03.information_behavior": {
    prompt: "担心伴侣的安全，或想知道 TA 的安排时，你通常会怎么做？",
    optionLabels: {
      every_outing_notice: "希望大多数时候出门前都能告诉我",
      punish_no_report: "如果对方没有告诉我，我容易生气，或做出带有惩罚意味的反应",
    },
  },
  "C04.third_party_acceptance": {
    prompt: "亲密的人和一个可能互相吸引的人单独吃饭，或长时间相处，你会怎么想？",
    optionLabels: {
      accept_need_context: "可以接受，但希望知道两人是什么关系",
      uncomfortable_set_boundary: "会明显不舒服，需要一起说清楚哪些可以、哪些不可以",
    },
  },
  "C04.third_party_behavior": {
    prompt: "如果是你和一个可能互相吸引的人单独吃饭，或长时间相处，你通常会怎么做？",
    optionLabels: {
      discuss_if_recurring: "如果开始经常这样相处，会主动和伴侣讨论边界",
      renegotiate_if_uncomfortable: "如果伴侣明显不舒服，倾向于一起重新商量边界",
      avoid_one_on_one: "关系稳定后，通常会避开这种单独相处",
      depends_context: "要看具体是谁、是什么情况",
    },
  },
  "D01.initial_conflict": {
    prompt: "和一个重要的人发生明显冲突时，你通常会怎么做？",
    optionLabels: {
      space_return: "明确说自己需要独处，约好之后再谈",
      wait_other: "等对方先来修复关系",
    },
  },
  "D02.repair_need": {
    prompt: "对方怎么做，最能帮助你重新信任 TA？",
    optionLabels: {
      impact_acknowledgement: "直接承认这件事造成的影响",
      compensation: "做些具体的事来补偿或补救",
    },
  },
  "D02.immediate_repair_style": {
    prompt: "意识到自己的行为伤害了对方时，你通常最先做什么？",
  },
  "D02.repair_follow_through": {
    prompt: "开始修复关系后，哪些事是你通常能持续做到的？",
    optionLabels: {
      compensation: "做些具体的事来补偿或补救",
      time: "给对方足够时间，不催 TA 原谅",
    },
  },
  "D03.pause_entry": {
    prompt: "需要暂时停下来、不再继续聊时，你通常会怎么做？",
    optionLabels: {
      explicit_request: "明确告诉对方，我需要暂停一下",
      full_withdrawal: "暂时完全不交流，等自己准备好了再说",
      oscillate: "嘴上说需要空间，却又断断续续地继续争论",
    },
  },
  "D03.reassurance_supply": {
    prompt: "暂停时，你通常会把哪些事明确告诉对方？",
    optionLabels: {
      rough_time: "大概什么时候可以接着聊",
      basic_response: "暂停期间仍会有基本的回应",
    },
  },
  "D03.return_timing": {
    prompt: "如果需要暂停，你通常过多久能重新开始讨论？",
  },
  "D03.reassurance_need": {
    prompt: "冲突后，对方说想暂时独处。TA 至少需要怎么做，你才不会觉得关系正在破裂？",
    optionLabels: {
      rough_time: "说一下大概什么时候能接着聊",
      cannot_pause: "冲突后暂停交流，我很难接受",
    },
  },
  "D03.silence_tolerance": {
    prompt: "没有任何解释，多久不联系会让你觉得关系可能出了严重问题？",
  },
  "E01.clarity_action": {
    prompt: "已经愉快地见过几次，但谁都没说接下来想怎样。你通常会怎么做？",
    optionLabels: {
      express_interest: "说出自己愿意继续了解，但不要求马上确定是什么关系",
      let_natural: "继续相处，等关系自然变得清楚",
    },
  },
  "E01.definition_need": {
    prompt: "持续见面、也已经亲近起来后，你最希望怎样？",
    optionLabels: {
      boundaries_without_label: "不急着确定叫什么关系，但重要的边界要说清楚",
      natural_no_define: "让关系自然发展，不需要正式确定关系",
      behavior_over_label: "只要行为稳定，有没有给这段关系一个明确的称呼并不重要",
    },
  },
  "E01.fast_vs_unclear": {
    prompt: "哪一种情况更容易让你没有安全感？",
  },
  "E02.offline_pace": {
    prompt: "在 Perhaps 认识一个人，聊得不错，你通常希望多久后见面？",
    optionLabels: {
      days: "几天内，尽快看看现实中相处的感觉",
      no_fixed: "没有固定时间，看聊得怎么样",
      depends_distance_safety: "要看距离，以及见面是否安全",
    },
  },
  "E03.physical_timing": {
    prompt: "双方都已成年、边界清楚，也彼此有感觉时，你对身体亲密的节奏更接近哪一种？",
  },
  "E03.physical_meaning": {
    prompt: "对你来说，身体亲密通常意味着什么？",
    optionLabels: {
      toward_exclusive: "通常意味着双方应该逐渐只和彼此交往",
      after_definition: "只有明确关系以后才适合发生",
    },
  },
  "E04.multidating_acceptance": {
    prompt: "还没约定只和彼此交往时，你能接受双方同时了解其他可能发展关系的人吗？",
    optionLabels: {
      focus_after_repeat: "一旦持续见面，我希望双方专心了解彼此",
    },
  },
  "E04.multidating_behavior": {
    prompt: "还没约定只和彼此交往时，你通常会怎么做？",
    optionLabels: {
      early_then_focus: "刚开始可能会同时了解几个人，但持续见面后，通常会逐渐专心了解一个人",
    },
  },
  "E04.disclosure_supply": {
    prompt: "还没约定只和彼此交往时，如果也在了解其他人，你通常会怎么处理？",
    optionLabels: {
      when_meaningful: "开始持续见面，或明显投入更多感情时，会主动说明",
      no_special_disclosure: "还没约定只和彼此交往时，我通常觉得不必特别说明",
    },
  },
  "F01.high_impact_behaviors": {
    prompt: "哪些行为一旦发生，最可能让你不想继续这段关系？",
    optionLabels: {
      deliberate_deception: "故意欺骗",
      outside_boundary_intimacy: "做出超出关系边界的亲密行为",
      chronic_refusal_accountability: "长期不肯为自己的行为造成的影响负责",
    },
  },
  "F01.hard_boundary_authorizations": {
    prompt: "刚才选的行为中，哪些要明确设为不可协商的边界？",
    optionLabels: {
      deliberate_deception: "故意欺骗",
      repeated_disappearance_after_conflict: "冲突后反复消失",
      control_restrict_life: "检查、控制或限制我的生活",
      public_humiliation: "在他人面前羞辱我",
      privacy_breach: "泄露我私下告诉对方的事情",
      outside_boundary_intimacy: "做出超出关系边界的亲密行为",
      chronic_refusal_accountability: "长期不肯为自己的行为造成的影响负责",
    },
  },
  "F02.positive_priorities": {
    prompt: "什么最影响你愿不愿意继续了解一个人？",
    optionLabels: {
      agency_coordination: "主动做事，也愿意协调",
      world_entry_curiosity: "对彼此有好奇心，愿意了解对方的世界",
    },
  },
  "F02.operation_priorities": {
    prompt: "相处时，哪些方面最影响你愿不愿意继续这段关系？",
    optionLabels: {
      conflict_repair: "怎么处理冲突、修复关系",
      clarity_exclusivity: "是否把关系说清楚、是否只和彼此交往",
      daily_shared_rhythm: "日常怎么过，两个人的生活节奏是否合得来",
      decision_coordination: "怎么一起做决定、协调意见",
      shared_meaning: "了解彼此的世界，有共同在意的事",
    },
  },
  "F03.tolerances": {
    prompt: "两个人有哪些不同，你最能接受？",
    optionLabels: { relationship_definition_pace: "什么时候把关系说清楚" },
  },
  "F04.positive_moment": {
    prompt: "有没有哪个瞬间，让你突然觉得“我想继续了解这个人”？说说当时发生了什么。",
  },
  "F05.relationship_decay": {
    prompt: "怎样的相处会让你逐渐失去兴趣，或想退出这段关系？",
  },
  "F06.temporal_change": {
    prompt: "有没有哪些描述以前符合你，现在却不完全适用了？发生了什么变化？",
  },
  "F06.system_misread": {
    prompt: "填完这份问卷，你觉得系统最可能在哪一点上误解你？",
  },
  "F06.unmodeled_condition": {
    prompt: "有没有什么现实条件，会明显影响你开始一段关系，前面的选项却没能准确表达？",
  },
};

function displayText(text: string): string {
  return text.replace(/\bta\b/g, "TA");
}

function applyFieldCopy(field: ClientField): ClientField {
  const { optionLabels, ...copy } = FIELD_COPY[field.id] ?? {};
  return {
    ...field,
    ...copy,
    ...(field.options ? {
      options: field.options.map((option) => ({
        ...option,
        label: displayText(optionLabels?.[option.id] ?? option.label),
      })),
    } : {}),
  };
}

function applyUnitCopy(unit: ClientUnit, version: string): ClientUnit {
  return {
    ...unit,
    title: version === "1.2.0" && unit.id === "F01" ? "影响很大的行为" : UNIT_TITLES[unit.id] ?? unit.title,
    fields: unit.fields.map((field) => {
      const display = applyFieldCopy(field);
      // The successor changed F01's measurement. Retain its new prompt rather
      // than applying the old question about wanting to end a relationship.
      return version === "1.2.0" && unit.id === "F01" ? { ...display, prompt: field.prompt } : display;
    }),
  };
}

export function applyQuestionnaireCopy(definition: QuestionnaireDefinition): QuestionnaireDefinition {
  // Historical submissions continue to display their original questionnaire.
  if (definition.version !== "1.1.1" && definition.version !== "1.2.0") return definition;
  return {
    ...definition,
    privacyPromise: "原始回答不会向其他参与者公开。",
    intro: {
      title: "在关系发生之前",
      body: ["没回答、不确定或不愿回答，都会保留为信息不足，不会因此认定两个人不合适。"],
    },
    context: {
      ...definition.context,
      intro: "没回答、不确定或不愿回答，都会保留为信息不足，不会因此认定两个人不合适。",
    },
    sections: definition.sections.map((section) => ({
      ...section,
      title: SECTION_TITLES[section.id] ?? section.title,
    })),
    units: definition.units.map((unit) => applyUnitCopy(unit, definition.version)),
    conditionalUnits: definition.conditionalUnits.map((unit) => applyUnitCopy(unit, definition.version)),
  };
}

// The submitted value remains the canonical text; only its visible label changes.
export function submissionConfirmationLabel(value: string): string {
  const labels = new Map([
    ["我已经检查过自己的答案", "我已检查自己的回答"],
    ["我理解问卷提交后无法修改", "我理解，提交后不能修改回答"],
    ["我理解匹配结果只是一种关系假设", "我理解，匹配结果不代表两个人一定合适"],
  ]);
  return labels.get(value) ?? value;
}
